/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useMutation } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';

// Lib
import { apiClient } from '@lib/api-client';
import { queryClient } from '@lib/react-query';

// Types
import {
	TApiError,
	TBodyDeleteMultipleItems,
	TResponseDeleteMultipleItems,
} from '@global-types/api/api';

// Constants
import { queryKeys } from '@config/constants/query-keys';

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const removeMultiple = (
	data: TBodyDeleteMultipleItems
): Promise<TResponseDeleteMultipleItems> => {
	return apiClient.delete(`/conversion_factors`, {
		data,
	});
};

/* -------------------------------------------------------------------------- */
/*                                     Hook                                   */
/* -------------------------------------------------------------------------- */
export const useRemoveMultiple = (
	page: number,
	size: number
) => {
	const { t } = useTranslation();
	const {
		mutate,
		isPending: isLoading,
		isIdle,
	} = useMutation({
		mutationFn: removeMultiple,
		onSuccess: () => {
			notifications.show({
				title: t('global.success'),
				message: t(
					'conversionFactors.notifications.conversionFactorSuccessfullyDeleted'
				),
				color: 'success',
			});
			queryClient.invalidateQueries({
				queryKey: [
					queryKeys.app.conversionFactors.list.key,
					{ page: page, size: size },
				],
			});
		},
		onError: (error: TApiError) => {
			notifications.show({
				title: t('global.error'),
				message: error?.type
					? t(`api.${error.type}`)
					: t(`api.null`),
				color: 'error',
			});
		},
	});

	return { mutate, isLoading, isIdle };
};
